// Media previews: the picture a cited post carries, on the feed and the cards,
// and the post itself — clip playing, album whole — one click away (064).
//
// The ▶/◳ glyphs said WHICH citation carries footage, and reading it meant a
// click out to Telegram per post. The thumbnails come from our own server
// (/api/live/thumb: cut at ingest from the bytes the recycled-media check
// already downloads, filled from the post's embed page on first view), so a
// scrolling feed never makes the reader's browser talk to Telegram. The viewer
// does — Telegram's own embed of the post is the one surface that plays the
// clip and shows every photo of an album — but only when the reader asks for
// it, which is the disclosure clicking the source link always was.
//
// Three rules the first versions taught (065, then 2026-09-30):
//   * ONE THUMBNAIL PER PICTURE. Channels repost each other's footage, and a
//     situation five channels reported carried five copies of one frame. Posts
//     whose pictures hash within the recycled-media threshold are one, the
//     rest counted on it (×3).
//   * A ROW SHOWS ONE TINY THUMBNAIL, BESIDE ITS WORDS. The first version put
//     a strip of thumbnails under every row, which took more room than the
//     feed had; the second collapsed it behind a "▶ 3" chip, which was one
//     more word among the many in a row's side column, and nobody could tell
//     which reports had footage. Now the row carries the lead picture at
//     48 px (a clip before a still, +N for the rest), and every picture is in
//     the row's details. The feed's 🖼 turns them off for text only.
//   * SMALL WHERE SMALL IS DRAWN. A row draws at most 64 px, so it asks for
//     the 160 px cut: a phone decoding a 400 px image for every row ran out of
//     memory. On a touch device the viewer shows our picture and loads
//     Telegram's embed — a whole page, a video player — only on "Play here".

import './media.css';
import { get, type EntityDto } from './api';
import { channelLabel, sourceLinks } from './livekinds';

export interface MediaItem {
  /** The event citing the post: the thumbnail route authorises through it. */
  eventId: string;
  /** The post's canonical t.me URL. */
  url: string;
  channel: string;
  media: 'video' | 'photo';
}

/** One PICTURE: the post shown for it, and the reposts folded into it. */
export interface MediaGroup {
  item: MediaItem;
  also: MediaItem[];
}

// Only a Telegram post has a picture we can fetch or an embed we can show;
// the same pattern the server enforces (mediathumb.post_parts).
const POST_RE = /^https:\/\/t\.me\/([A-Za-z0-9_]{3,64})\/(\d{1,12})$/;

/** The two sizes the server cuts (mediathumb.SIZES). */
export const ROW_PX = 160;
export const CARD_PX = 400;

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

/** Every cited post with its own picture across these events, in their
 *  order (the feed hands them newest first), one entry per post. */
export function mediaItems(events: EntityDto[]): MediaItem[] {
  const out: MediaItem[] = [];
  const seen = new Set<string>();
  for (const e of events) {
    for (const s of sourceLinks(e.attrs)) {
      if (!s.media || seen.has(s.url) || !POST_RE.test(s.url)) continue;
      seen.add(s.url);
      out.push({ eventId: e.id, url: s.url, channel: s.channel, media: s.media });
    }
  }
  return out;
}

export function thumbSrc(item: MediaItem, px = CARD_PX): string {
  return `/api/live/thumb?event=${encodeURIComponent(item.eventId)}`
    + `&post=${encodeURIComponent(item.url)}${px === CARD_PX ? '' : `&w=${px}`}`;
}

// --- one thumbnail per picture ------------------------------------------------

/** Bits at or under which two dHashes are one picture — mediasig.MAX_HAMMING,
 *  the threshold the recycled-footage check already stands on. */
const SAME_PICTURE_BITS = 6;
const hashes = new Map<string, string>();    // post URL -> dHash, 16 hex digits
const askedAt = new Map<string, number>();   // event id -> when its hashes were asked
// A picture unknown when asked may be known minutes later: a thumbnail filled
// on first view brings its hash.
const REASK_MS = 5 * 60_000;

function popcount32(n: number): number {
  let x = n >>> 0;
  x -= (x >>> 1) & 0x55555555;
  x = (x & 0x33333333) + ((x >>> 2) & 0x33333333);
  x = (x + (x >>> 4)) & 0x0f0f0f0f;
  return Math.imul(x, 0x01010101) >>> 24;
}

/** Differing bits between two 64-bit hashes written as 16 hex digits — in two
 *  32-bit halves, since a JS number cannot hold 64 bits exactly. */
export function hammingHex(a: string, b: string): number {
  const hi = parseInt(a.slice(0, 8), 16) ^ parseInt(b.slice(0, 8), 16);
  const lo = parseInt(a.slice(8, 16), 16) ^ parseInt(b.slice(8, 16), 16);
  return popcount32(hi) + popcount32(lo);
}

/** The items as PICTURES: a repost of a picture already listed folds into it.
 *  A clip outranks a still of the same frame (the clip is what a click should
 *  play); otherwise the first — the newest event's — stands. A post whose
 *  hash is not known yet stands alone: an unknown is never a duplicate. */
export function distinctMedia(items: MediaItem[],
                              known: ReadonlyMap<string, string> = hashes): MediaGroup[] {
  const groups: { g: MediaGroup; hash?: string }[] = [];
  for (const item of items) {
    const h = known.get(item.url);
    const same = h === undefined ? undefined
      : groups.find((x) => x.hash !== undefined
          && hammingHex(x.hash, h) <= SAME_PICTURE_BITS);
    if (!same) {
      groups.push({ g: { item, also: [] }, hash: h });
    } else if (item.media === 'video' && same.g.item.media !== 'video') {
      same.g.also.push(same.g.item);
      same.g.item = item;
    } else {
      same.g.also.push(item);
    }
  }
  return groups.map((x) => x.g);
}

export function mediaGroups(events: EntityDto[]): MediaGroup[] {
  return distinctMedia(mediaItems(events));
}

let hashesPending = false;

/** Learn the picture hashes of these events' media — 200 events per call,
 *  each asked at most once per REASK_MS. Resolves true when something new
 *  arrived, which is the caller's cue to repaint. */
export async function learnMediaHashes(events: EntityDto[]): Promise<boolean> {
  if (hashesPending) return false;
  const now = Date.now();
  const ask = events.filter((e) => {
    const at = askedAt.get(e.id);
    if (at !== undefined && now - at < REASK_MS) return false;
    return mediaItems([e]).some((i) => !hashes.has(i.url));
  }).slice(0, 200).map((e) => e.id);
  if (!ask.length) return false;
  hashesPending = true;
  for (const id of ask) askedAt.set(id, now);
  try {
    const res = await get<{ hashes?: Record<string, string> }>(
      `/api/live/media-hashes?ids=${ask.join(',')}`);
    let fresh = false;
    for (const [url, h] of Object.entries(res.hashes ?? {})) {
      if (/^[0-9a-f]{16}$/.test(h) && hashes.get(url) !== h) {
        hashes.set(url, h);
        fresh = true;
      }
    }
    return fresh;
  } catch {
    return false;   // a caution about duplicates must never break a card
  } finally {
    hashesPending = false;
  }
}

// --- thumbnails on rows, strips on cards ------------------------------------------

// Thumbnails on feed rows are ON unless the reader turned them off (the 🖼
// button); off, a row with footage still carries a mark that says so.
const THUMBS_KEY = 'cockpit-feed-thumbs';
let rowThumbs = (() => {
  try { return localStorage.getItem(THUMBS_KEY) !== '0'; } catch { return true; }
})();

export function feedThumbsOn(): boolean {
  return rowThumbs;
}

export function setFeedThumbs(on: boolean): void {
  rowThumbs = on;
  try { localStorage.setItem(THUMBS_KEY, on ? '1' : '0'); } catch { /* per-browser nicety */ }
}

// A CARD's strip is open unless its chip closed it: a card is opened one at a
// time, on purpose, and its pictures are what it is about.
const closed = new Set<string>();

export function isMediaOpen(key: string): boolean {
  return !closed.has(key);
}

function setOpen(key: string, open: boolean): void {
  if (open) closed.delete(key);
  else closed.add(key);
}

// --- rendering ---------------------------------------------------------------------

// A thumbnail that answered 404 this session (the post had no picture after
// all, or Telegram would not give one) is not drawn again: the feed repaints on
// every poll, and a box that flickers in and out each time reads as broken.
const missing = new Set<string>();

function postLabel(item: MediaItem): string {
  const n = POST_RE.exec(item.url)?.[2];
  return `${channelLabel(item.channel) || 'post'}${n ? ` · #${n}` : ''}`;
}

function shown(groups: MediaGroup[]): MediaGroup[] {
  return groups.filter((g) => !missing.has(g.item.url));
}

function alsoLine(g: MediaGroup): string {
  if (!g.also.length) return '';
  const who = [...new Set(g.also.map((i) => channelLabel(i.channel) || 'post'))];
  return `also posted by ${who.join(', ')}`;
}

/** "3 pictures incl. video from 5 posts" — what a chip or thumbnail holds. */
function mediaSummary(g: MediaGroup[]): string {
  const video = g.some((x) => x.item.media === 'video');
  const posts = g.reduce((n, x) => n + 1 + x.also.length, 0);
  return `${g.length} ${g.length === 1 ? 'picture' : 'pictures'}`
    + `${video ? ' incl. video' : ''}${posts > g.length ? ` from ${posts} posts` : ''}`;
}

function chipState(groups: MediaGroup[], key: string)
    : { open: boolean; title: string; label: string } | null {
  const g = shown(groups);
  if (!g.length) return null;
  const video = g.some((x) => x.item.media === 'video');
  const open = isMediaOpen(key);
  return {
    open,
    title: `${mediaSummary(g)} — ${open ? 'hide' : 'show'}`,
    label: `${video ? '▶' : '◳'} ${g.length}<i aria-hidden="true">${open ? '▾' : '▸'}</i>`,
  };
}

/** A card's chip: the strongest kind and how many PICTURES, with a caret
 *  saying which way a click goes. '' when there is nothing to show. */
export function mediaChipHtml(groups: MediaGroup[], key: string): string {
  const st = chipState(groups, key);
  if (!st) return '';
  return `<button type="button" class="media-chip${st.open ? ' open' : ''}"
    data-mkey="${esc(key)}" aria-expanded="${st.open}"
    title="${esc(st.title)}">${st.label}</button>`;
}

/** Every picture as a strip, up to `max` then a +N that opens the viewer
 *  there — a row's details, and a card's open slot. */
export function mediaStripHtml(groups: MediaGroup[], max: number, px: number): string {
  const g = shown(groups);
  if (!g.length) return '';
  const thumbs = g.slice(0, max).map((x) => {
    const i = x.item;
    const also = alsoLine(x);
    return `<button type="button" class="media-thumb" data-post="${esc(i.url)}"
      title="${esc(`${postLabel(i)} — ${i.media}${also ? `, ${also}` : ''}. Click to view the post`)}"><img
      src="${esc(thumbSrc(i, px))}" alt="" loading="lazy" decoding="async">${
      i.media === 'video' ? '<i class="mt-play" aria-hidden="true">▶</i>' : ''}${
      x.also.length ? `<b class="mt-dup">×${x.also.length + 1}</b>` : ''}</button>`;
  }).join('');
  const rest = g.length - max;
  const more = rest > 0
    ? `<button type="button" class="media-more" data-post="${esc(g[max]!.item.url)}"
        title="${rest} more — open the viewer">+${rest}</button>`
    : '';
  return `<span class="media-strip">${thumbs}${more}</span>`;
}

/** Where a key's strip goes: filled now when open, by its chip when opened. */
export function mediaSlotHtml(groups: MediaGroup[], key: string,
                              opts: { max: number; px: number }): string {
  if (!shown(groups).length) return '';
  return `<span class="media-slot" data-mslot="${esc(key)}" data-max="${opts.max}"
    data-px="${opts.px}">${isMediaOpen(key) ? mediaStripHtml(groups, opts.max, opts.px) : ''}</span>`;
}

// A picture filled just now has a hash now: ask again on the next pass.
function noteLoaded(groups: MediaGroup[], url: string): void {
  const item = groups.find((g) => g.item.url === url)?.item;
  if (item && !hashes.has(url)) askedAt.delete(item.eventId);
}

/** Wire the strip inside `slot`: a thumbnail opens the viewer and stops there
 *  — inside a feed row it must not also fly the map. */
export function wireMediaStrip(slot: HTMLElement, groups: MediaGroup[]): void {
  slot.querySelectorAll<HTMLElement>('.media-strip > .media-thumb, .media-more').forEach((btn) => {
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const list = shown(groups);
      const at = list.findIndex((g) => g.item.url === btn.dataset.post);
      openMediaViewer(list, Math.max(0, at));
    });
    const img = btn.querySelector('img');
    if (!img) return;
    const url = btn.dataset.post ?? '';
    img.addEventListener('error', () => {
      missing.add(url);
      const strip = btn.closest('.media-strip');
      btn.remove();
      if (strip && !strip.querySelector('.media-thumb')) strip.remove();
    }, { once: true });
    img.addEventListener('load', () => noteLoaded(groups, url), { once: true });
  });
}

/** A feed row's media: ONE tiny thumbnail — the lead picture (the first clip,
 *  else the first picture: a clip is what "is there footage?" is usually
 *  asking about), ▶ on a clip, +N for the rest — or, with thumbnails off, a
 *  mark ("▶ 3"). Either opens the viewer on the lead picture. '' when there
 *  is nothing to show. */
export function feedThumbHtml(groups: MediaGroup[]): string {
  const g = shown(groups);
  if (!g.length) return '';
  const lead = g.find((x) => x.item.media === 'video') ?? g[0]!;
  const title = esc(`${mediaSummary(g)} — click to view`);
  if (!rowThumbs) {
    return `<button type="button" class="feed-mark" data-post="${esc(lead.item.url)}"
      title="${title}">${lead.item.media === 'video' ? '▶' : '◳'}${
      g.length > 1 ? ` ${g.length}` : ''}</button>`;
  }
  return `<button type="button" class="media-thumb feed-thumb" data-post="${
    esc(lead.item.url)}" title="${title}"><img src="${esc(thumbSrc(lead.item, ROW_PX))}"
    alt="" loading="lazy" decoding="async">${lead.item.media === 'video'
      ? '<i class="mt-play" aria-hidden="true">▶</i>' : ''}${
      g.length > 1 ? `<b class="ft-n">+${g.length - 1}</b>` : ''}</button>`;
}

/** Wire the row's thumbnail (or mark), a direct child of `row`. A click opens
 *  the viewer and stops there; a picture that will not load gives its place
 *  to the next one, or to nothing. */
export function wireFeedThumb(row: HTMLElement, groups: MediaGroup[]): void {
  const btn = row.querySelector<HTMLElement>(':scope > .feed-thumb, :scope > .feed-mark');
  if (!btn) return;
  const url = btn.dataset.post ?? '';
  btn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    const list = shown(groups);
    openMediaViewer(list, Math.max(0, list.findIndex((g) => g.item.url === url)));
  });
  const img = btn.querySelector('img');
  if (!img) return;
  img.addEventListener('error', () => {
    missing.add(url);
    btn.insertAdjacentHTML('afterend', feedThumbHtml(groups));
    btn.remove();
    wireFeedThumb(row, groups);
  }, { once: true });
  img.addEventListener('load', () => noteLoaded(groups, url), { once: true });
}

/** Wire one key's chip and slot inside `root`: the chip opens and closes the
 *  strip in place (no repaint around it), a thumbnail opens the viewer. */
export function wireMedia(root: ParentNode, key: string, groups: MediaGroup[]): void {
  const sel = CSS.escape(key);
  const slot = root.querySelector<HTMLElement>(`[data-mslot="${sel}"]`);
  const chip = root.querySelector<HTMLElement>(`[data-mkey="${sel}"]`);
  if (slot) wireMediaStrip(slot, groups);
  chip?.addEventListener('click', (ev) => {
    ev.stopPropagation();
    const open = !isMediaOpen(key);
    setOpen(key, open);
    const st = chipState(groups, key);
    if (!st) {
      chip.remove();
    } else {
      chip.classList.toggle('open', st.open);
      chip.setAttribute('aria-expanded', String(st.open));
      chip.title = st.title;
      chip.innerHTML = st.label;
    }
    if (slot) {
      slot.innerHTML = open
        ? mediaStripHtml(groups, Number(slot.dataset.max) || 4, Number(slot.dataset.px) || ROW_PX)
        : '';
      if (open) wireMediaStrip(slot, groups);
    }
  });
}

/** A card's media, header chip and strip together, in `host` — and repainted
 *  in place once the hashes arrive, since a card is built before they have. */
export function mountMedia(host: HTMLElement, key: string, events: EntityDto[],
                           opts: { max: number; px: number }): void {
  const paint = (): void => {
    const groups = mediaGroups(events);
    host.innerHTML = groups.length
      ? `<div class="media-card-head">${mediaChipHtml(groups, key)}</div>${
          mediaSlotHtml(groups, key, opts)}`
      : '';
    wireMedia(host, key, groups);
  };
  paint();
  void learnMediaHashes(events).then((fresh) => {
    if (fresh && host.isConnected) paint();
  });
}

// --- the viewer --------------------------------------------------------------------

interface Viewer {
  el: HTMLElement;
  groups: MediaGroup[];
  index: number;
  teardown: () => void;
}
let viewer: Viewer | null = null;

export function closeMediaViewer(): void {
  viewer?.teardown();
  viewer?.el.remove();
  viewer = null;
}

function embedUrl(item: MediaItem): string {
  const dark = document.documentElement.dataset.theme === 'dark';
  return `${item.url}?embed=1${dark ? '&dark=1' : ''}`;
}

/** Telegram's embed of the post, over the app. Our picture holds the space
 *  while it loads (and stays the picture if Telegram cannot be reached); ←/→
 *  step through the card's pictures; Esc, ✕ or a click outside closes. On a
 *  touch device the embed waits for "Play here": a phone already carrying the
 *  map has little room for a second page and a video player, and "Open in
 *  Telegram" hands the clip to the app built for it. */
export function openMediaViewer(groups: MediaGroup[], index: number): void {
  closeMediaViewer();
  if (!groups.length) return;
  const light = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const el = document.createElement('div');
  el.className = `media-viewer${light ? ' mv-light' : ''}`;
  el.innerHTML = `
    <div class="mv-panel" role="dialog" aria-modal="true" aria-label="Source post">
      <header class="mv-head">
        <span class="mv-title"></span>
        <span class="mv-count"></span>
        <a class="mv-open" target="_blank" rel="noopener noreferrer"
          title="Open the post on Telegram">${light ? 'Open in Telegram ↗' : 'Open ↗'}</a>
        <button type="button" class="mv-x" title="Close (Esc)">✕</button>
      </header>
      <div class="mv-also"></div>
      <div class="mv-stage">
        <img class="mv-ph" alt="">
        <button type="button" class="mv-play">▶ Play here</button>
      </div>
      <button type="button" class="mv-nav mv-prev" title="Previous (←)" aria-label="Previous">‹</button>
      <button type="button" class="mv-nav mv-next" title="Next (→)" aria-label="Next">›</button>
    </div>`;
  const stage = el.querySelector('.mv-stage') as HTMLElement;
  const ph = el.querySelector('.mv-ph') as HTMLImageElement;
  const play = el.querySelector('.mv-play') as HTMLButtonElement;
  let frame: HTMLIFrameElement | null = null;
  let grace: number | undefined;

  // The embed reports its own height (Telegram's widget-frame.js posts
  // {"event":"resize","height":N} to its parent); the stage caps it and
  // scrolls past that. Only messages from THIS frame are read.
  const onMessage = (ev: MessageEvent): void => {
    if (!frame || ev.origin !== 'https://t.me' || ev.source !== frame.contentWindow) return;
    let data: { event?: string; height?: number } | null = null;
    try {
      data = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
    } catch { return; }
    if (data?.event === 'resize' && typeof data.height === 'number'
        && Number.isFinite(data.height) && data.height > 0) {
      frame.style.height = `${Math.ceil(data.height)}px`;
      stage.classList.add('mv-loaded');
    }
  };

  const embed = (item: MediaItem): void => {
    window.clearTimeout(grace);
    frame?.remove();
    frame = document.createElement('iframe');
    frame.className = 'mv-frame';
    frame.title = 'Telegram post';
    frame.referrerPolicy = 'no-referrer';
    frame.setAttribute('sandbox',
      'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox');
    frame.setAttribute('allow', 'fullscreen; picture-in-picture');
    frame.allowFullscreen = true;
    // The thumbnail stays over the frame until the embed reports its size —
    // only Telegram's page does that, so where Telegram is unreachable the
    // reader keeps our picture instead of a browser error page. A page that
    // loaded but never reports is shown anyway after a grace period.
    frame.addEventListener('load', () => {
      window.clearTimeout(grace);
      grace = window.setTimeout(() => stage.classList.add('mv-loaded'), 1500);
    });
    frame.src = embedUrl(item);
    stage.classList.add('mv-embedding');
    stage.appendChild(frame);
  };

  const show = (i: number): void => {
    if (!viewer) return;
    const n = viewer.groups.length;
    viewer.index = ((i % n) + n) % n;
    const g = viewer.groups[viewer.index]!;
    const item = g.item;
    (el.querySelector('.mv-title') as HTMLElement).textContent =
      `${postLabel(item)} · ${item.media}`;
    (el.querySelector('.mv-count') as HTMLElement).textContent =
      n > 1 ? `${viewer.index + 1} / ${n}` : '';
    (el.querySelector('.mv-also') as HTMLElement).textContent = alsoLine(g);
    (el.querySelector('.mv-open') as HTMLAnchorElement).href = item.url;
    el.classList.toggle('mv-single', n < 2);
    window.clearTimeout(grace);
    frame?.remove();
    frame = null;
    stage.classList.remove('mv-loaded', 'mv-embedding');
    ph.hidden = missing.has(item.url);
    if (!ph.hidden) ph.src = thumbSrc(item, CARD_PX);
    play.textContent = item.media === 'video' ? '▶ Play here' : '◳ Show the post here';
    if (!light) embed(item);
  };

  // Capture phase and consumed: Esc is shared with drawing, the selection
  // and the console, and must close only this while it is open.
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') closeMediaViewer();
    else if (ev.key === 'ArrowLeft') show((viewer?.index ?? 0) - 1);
    else if (ev.key === 'ArrowRight') show((viewer?.index ?? 0) + 1);
    else return;
    ev.preventDefault();
    ev.stopImmediatePropagation();
  };
  window.addEventListener('message', onMessage);
  window.addEventListener('keydown', onKey, true);
  ph.addEventListener('error', () => { ph.hidden = true; });
  play.addEventListener('click', () => {
    if (viewer) embed(viewer.groups[viewer.index]!.item);
  });
  el.addEventListener('click', (ev) => { if (ev.target === el) closeMediaViewer(); });
  el.querySelector('.mv-x')!.addEventListener('click', closeMediaViewer);
  el.querySelector('.mv-prev')!.addEventListener('click', () =>
    show((viewer?.index ?? 0) - 1));
  el.querySelector('.mv-next')!.addEventListener('click', () =>
    show((viewer?.index ?? 0) + 1));

  viewer = {
    el, groups, index,
    teardown: () => {
      window.clearTimeout(grace);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('keydown', onKey, true);
    },
  };
  document.body.appendChild(el);
  show(index);
  (el.querySelector('.mv-x') as HTMLButtonElement).focus();
}
